import React from 'react'
import * as ROUTES from 'constants/routes'
import { Link, Text } from '@workwhile/ui'
import { ColumnDef } from '@tanstack/react-table'
import { formatInTimeZone } from 'date-fns-tz'
import { PaginatedTable } from 'components/Table/PaginatedTable'
import { JetCallReportItem } from 'api/admin/jet/listJetCallReports'
import { JetStatusBadge } from './JetStatusBadge'
import { JetReportDeadlineCell } from './JetReportDeadlineCell'
import { JetTimesheetPhotoCell } from './JetTimesheetPhotoCell'
import { useJetStatusContextValue } from '../context/JetStatusProvider'

const formatTime = (value: string | null, timezone: string) =>
  value ? formatInTimeZone(value, timezone, 'MMM d, h:mm a z') : '-'

const columns: ColumnDef<JetCallReportItem>[] = [
  {
    header: 'Shift',
    accessorKey: 'shiftId',
    cell: ({ row }) => (
      <Link
        to={ROUTES.SHIFT_DETAIL.replace(
          ':shiftId',
          row.original.shiftId.toString()
        )}
        target="_blank"
      >
        {row.original.shiftId}
      </Link>
    ),
  },
  {
    header: 'Worker ID',
    accessorKey: 'workerId',
    cell: ({ row }) => (
      <Link
        to={ROUTES.USER_DETAIL.replace(
          ':userId',
          row.original.workerId.toString()
        )}
        target="_blank"
      >
        {row.original.workerId}
      </Link>
    ),
  },
  {
    header: 'Association ID',
    accessorKey: 'associationId',
    // No work-item detail page exists; deep-links into the shift's worker row instead.
    cell: ({ row }) => (
      <Link
        to={`${ROUTES.SHIFT_DETAIL.replace(
          ':shiftId',
          row.original.shiftId.toString()
        )}#work-${row.original.associationId}`}
        target="_blank"
      >
        {row.original.associationId}
      </Link>
    ),
  },
  {
    header: 'Assignment ID',
    accessorKey: 'assignmentId',
    cell: ({ row }) =>
      row.original.assignmentId ?? <Text color="lightText">-</Text>,
  },
  {
    header: 'Started',
    accessorKey: 'startedAt',
    cell: ({ row }) => (
      <Text style={{ whiteSpace: 'nowrap' }}>
        {formatTime(row.original.startedAt, row.original.timezone)}
      </Text>
    ),
  },
  {
    header: 'Completed',
    accessorKey: 'completedAt',
    cell: ({ row }) => (
      <Text style={{ whiteSpace: 'nowrap' }}>
        {formatTime(row.original.completedAt, row.original.timezone)}
      </Text>
    ),
  },
  {
    header: 'Status',
    cell: ({ row }) => <JetStatusBadge item={row.original} />,
  },
  {
    header: 'Report deadline',
    cell: ({ row }) => <JetReportDeadlineCell item={row.original} />,
  },
  {
    header: 'Timesheet',
    cell: ({ row }) => (
      <JetTimesheetPhotoCell
        associationId={row.original.associationId}
        hasTimesheetPhoto={row.original.hasTimesheetPhoto}
        timesheetPhotoIsFallback={row.original.timesheetPhotoIsFallback}
      />
    ),
  },
]

export const JetStatusTable = () => {
  const {
    state: { data, pagination, isFetching },
    actions: { setPagination },
  } = useJetStatusContextValue()

  return (
    <PaginatedTable
      columns={columns}
      items={data.items}
      rowCount={data.totalRows}
      pagination={pagination}
      setPagination={setPagination}
      isFetching={isFetching}
      emptyMessage="No CDS shifts found."
      emptyStateHeight={50}
    />
  )
}
